import { IdentifiableProduct, ProductIdentifierCorrelation, identifierFor } from './productIdentifier'

import { GrundrissSchmiedeOrderCorrectionBillingDTO } from 'models/messageThreads'
import { VirtualVisitExtensionParamsDTO, VisualsSelectionDTO } from 'models/visuals'

/** Component name every log these builders emit is filed under. */
const LOG_COMPONENT = 'upsellPayloads'

/** What the caller knows about the billing side of an upsell it is about to send. */
interface BillingInput {
  /** The payment option the client picked. Absent when the request needs no payment. */
  billingProduct?: IdentifiableProduct
  /** Stripe payment intent, absent when the selected payment method does not use Stripe. */
  paymentIntentId?: string
  /** Ids carried into every log the builder emits. */
  correlation?: ProductIdentifierCorrelation
}

/** Arguments of {@link buildVisualsSelectionBody}. */
interface VisualsSelectionInput extends BillingInput {
  /** Names of the visuals being selected. */
  filenames: string[]
  /** The assignment's own product, which the selected visuals belong to. */
  product: IdentifiableProduct
}

/** Arguments of {@link buildVirtualVisitExtensionParams}. */
interface VirtualVisitExtensionInput extends BillingInput {
  billingProduct: IdentifiableProduct
}

/**
 * Builds the body of an additional-visuals selection, identifying the assignment product and the
 * billing product by their Product2 code alone.
 *
 * @throws ProductIdentifierError when a product carries no usable product code.
 */
export const buildVisualsSelectionBody = ({
  filenames,
  product,
  billingProduct,
  paymentIntentId,
  correlation = {},
}: VisualsSelectionInput): VisualsSelectionDTO => ({
  filenames,
  ...identifierFor(product, 'productCode', 'mission', {
    flow: 'Additional visuals upsell',
    component: LOG_COMPONENT,
    action: 'buildVisualsSelectionBody',
    correlation,
  }),
  ...(billingProduct
    ? identifierFor(billingProduct, 'primaryBillingProductCode', 'billing', {
      flow: 'Additional visuals upsell',
      component: LOG_COMPONENT,
      action: 'buildVisualsSelectionBody',
      correlation,
    })
    : {}),
  stripePaymentIntentId: paymentIntentId,
})

/**
 * Builds the query parameters of a virtual-visit hosting extension.
 *
 * @throws ProductIdentifierError when the billing product carries no usable product code.
 */
export const buildVirtualVisitExtensionParams = ({
  billingProduct,
  paymentIntentId,
  correlation = {},
}: VirtualVisitExtensionInput): VirtualVisitExtensionParamsDTO => ({
  stripePaymentIntentId: paymentIntentId,
  ...identifierFor(billingProduct, 'primaryBillingProductCode', 'billing', {
    flow: 'Virtual visit extension upsell',
    component: LOG_COMPONENT,
    action: 'buildVirtualVisitExtensionParams',
    correlation,
  }),
})

/**
 * Builds the billing fields of a floor-plan correction request. A correction that needs no payment
 * carries no billing product and therefore no billing field at all.
 *
 * @throws ProductIdentifierError when the billing product carries no usable product code.
 */
export const buildFloorPlanCorrectionBilling = ({
  billingProduct,
  paymentIntentId,
  correlation = {},
}: BillingInput): GrundrissSchmiedeOrderCorrectionBillingDTO => ({
  paymentIntentId,
  ...(billingProduct
    ? identifierFor(billingProduct, 'primaryBillingProductCode', 'billing', {
      flow: 'Floor plan correction upsell',
      component: LOG_COMPONENT,
      action: 'buildFloorPlanCorrectionBilling',
      correlation,
    })
    : {}),
})
